import { EntityInfo } from '@record-it-npm/synchro-common';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { RateLimit } from 'record-it-utils/utils/RateLimit/RateLimit';
import { StorageAdapter } from '../StorageAdapter/StorageAdapter';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';

export class EntityRevisionStorage<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> {
  private revisionInfoByEntityName: RevisionInfoByEntityName<TSynchronizationEnvironmentTypes> =
    {};
  private saveRevisionInfosRateLimited = RateLimit.rateLimitFunction(
    this.saveRevisionInfos.bind(this),
    50
  );

  private initialized: boolean = false;

  constructor(
    private readonly allEntityInfos: Array<
      EntityInfo<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]['entity']
      >
    >,
    private readonly getRevisionInfoStorageKeyForEntityName: (
      entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
    ) => string
  ) {}

  public async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.loadRevisionInfos();

    this.initialized = true;
  }

  public destroy(): void {
    if (this.saveRevisionInfosRateLimited.isPending()) {
      throw new Error(
        "can't destroy the EntityRevisionStorage when it's there is still a save pending"
      );
    }

    this.revisionInfoByEntityName = {};
  }

  public getRevision(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  ): number {
    this.assertInitialized();

    const info = this.revisionInfoByEntityName[entityName];
    return info?.revision ?? 0;
  }

  public updateRevision(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    revision: number
  ): void {
    this.assertInitialized();

    const entityInfo = this.allEntityInfos.find(
      (info) => info.entityName === entityName
    );
    assertNotNullOrUndefined(
      entityInfo,
      `no entity info found for "${entityName}"`
    );

    if (entityInfo.trackGlobalRevision) {
      this.modifyRevisionInfo(entityName, (info) => {
        if (revision > info.revision) {
          info.revision = revision;
        }
      });
    }
  }

  public getRelativeRevision(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityId: EntityManagerBaseEntity['id']
  ): number {
    this.assertInitialized();

    const info = this.revisionInfoByEntityName[entityName];
    const relativeRevisionById = info?.relativeRevisions[relativeEntityName];
    return relativeRevisionById?.[relativeEntityId] ?? 0;
  }

  public updateRelativeRevision(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityId: EntityManagerBaseEntity['id'],
    revision: number
  ): void {
    this.assertInitialized();

    this.modifyRevisionInfo(entityName, (info) => {
      let relativeInfo: RevisionById | undefined =
        info.relativeRevisions[relativeEntityName];
      if (!relativeInfo) {
        relativeInfo = info.relativeRevisions[relativeEntityName] = {};
      }

      if (revision > (relativeInfo[relativeEntityId] ?? 0)) {
        relativeInfo[relativeEntityId] = revision;
      }
    });
  }

  public resetRelativeRevision(
    relativeEntityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    relativeEntityId: EntityManagerBaseEntity['id']
  ): void {
    let modified = false;

    const revisionInfos = Object.values(this.revisionInfoByEntityName) as Array<
      EntityRevisionInfo<TSynchronizationEnvironmentTypes>
    >;
    for (const revisionInfo of revisionInfos) {
      const revisionById = revisionInfo.relativeRevisions[relativeEntityName];
      if (revisionById && relativeEntityId in revisionById) {
        delete revisionById[relativeEntityId];
        modified = true;
      }
    }

    if (modified) {
      this.saveRevisionInfosRateLimited();
    }
  }

  public async flush(): Promise<void> {
    if (this.saveRevisionInfosRateLimited.isPending()) {
      this.saveRevisionInfosRateLimited.cancel();
      await this.saveRevisionInfos();
    }
  }

  private modifyRevisionInfo(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    modifyFunction: (
      revisionInfo: EntityRevisionInfo<TSynchronizationEnvironmentTypes>
    ) => void
  ): void {
    let info: EntityRevisionInfo<TSynchronizationEnvironmentTypes> | undefined =
      this.revisionInfoByEntityName[entityName];

    if (!info) {
      info = this.revisionInfoByEntityName[entityName] = {
        revision: 0,
        relativeRevisions: {}
      };
    }

    modifyFunction(info);

    this.saveRevisionInfosRateLimited();
  }

  private async saveRevisionInfos(): Promise<void> {
    this.assertInitialized();

    const entityNames = Object.keys(this.revisionInfoByEntityName) as Array<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
    >;

    for (const entityName of entityNames) {
      const storageKey =
        this.getRevisionInfoStorageKeyForEntityName(entityName);
      await StorageAdapter.setGeneralItem(
        storageKey,
        this.revisionInfoByEntityName[entityName]
      );
    }
  }

  private async loadRevisionInfos(): Promise<void> {
    for (const entityInfo of this.allEntityInfos) {
      const storageKey = this.getRevisionInfoStorageKeyForEntityName(
        entityInfo.entityName
      );
      const info = (await StorageAdapter.getGeneralItem(storageKey)) as
        | EntityRevisionInfo<TSynchronizationEnvironmentTypes>
        | undefined;
      if (info) {
        this.revisionInfoByEntityName[entityInfo.entityName] = {
          ...info,
          revision: entityInfo.trackGlobalRevision ? info.revision : 0
        };
      } else {
        this.revisionInfoByEntityName[entityInfo.entityName] = {
          revision: 0,
          relativeRevisions: {}
        };
      }
    }
  }

  private assertInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "can't use the EntityRevisionManager because it isn't initialized"
      );
    }
  }
}

export type RevisionInfoByEntityName<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = Partial<
  Record<
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    EntityRevisionInfo<TSynchronizationEnvironmentTypes>
  >
>;

export type EntityRevisionInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = {
  revision: number;
  relativeRevisions: RelativeRevisionByEntityName<TSynchronizationEnvironmentTypes>;
};

export type RelativeRevisionByEntityName<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = Partial<
  Record<
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    RevisionById
  >
>;

export type RevisionById = Partial<
  Record<EntityManagerBaseEntity['id'], number>
>;
